import {
  ArrowsLeftRightIcon,
  BankIcon,
  BookOpenIcon,
  CalculatorIcon,
  CalendarCheckIcon,
  ChartLineUpIcon,
  ClipboardTextIcon,
  GearIcon,
  HourglassIcon,
  LightningIcon,
  PackageIcon,
  ScalesIcon,
  SlidersIcon,
  TreeStructureIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import type { ComponentType } from "react";

/**
 * Single source for the general-ledger screens. The sidebar entry, the
 * `/ledger` hub and the section tabs on every ledger page all read this list,
 * so a screen is added, renamed or gated in exactly one place.
 *
 * Gating mirrors the old sidebar rows one-to-one:
 *   supplierOnly — hidden from BUYER businesses (cost-adjacent / seller-only)
 *   ledgerOnly   — hidden while business_ledger_config.mode !== "INTERNAL";
 *                  only the AR ledger and the ledger settings stay reachable,
 *                  the latter being the one door that switches the ledger on.
 */
export type LedgerGroupKey = "book" | "report" | "intercompany" | "settings";

export type LedgerSection = {
  key: string;
  group: LedgerGroupKey;
  /** layout namespace — also the tab label. */
  titleKey: string;
  /** layout namespace — hub row description. */
  descKey: string;
  icon: ComponentType<any>;
  link: string;
  menu: string;
  supplierOnly: boolean;
  ledgerOnly: boolean;
};

export const LEDGER_HUB_LINK = "/ledger";

const s = (
  group: LedgerGroupKey,
  key: string,
  titleKey: string,
  icon: ComponentType<any>,
  link: string,
  supplierOnly: boolean,
  ledgerOnly: boolean,
): LedgerSection => ({
  key,
  group,
  titleKey,
  descKey: `ledgerHub.desc.${key}`,
  icon,
  link,
  menu: "INVOICE_SETTLEMENT",
  supplierOnly,
  ledgerOnly,
});

export const LEDGER_GROUP_ORDER: LedgerGroupKey[] = ["book", "report", "intercompany", "settings"];

export const LEDGER_SECTIONS: LedgerSection[] = [
  s("book", "journal", "nav.ledgerJournal", BookOpenIcon, "/ledger/journal", false, true),
  s("book", "accounts", "nav.ledgerAccounts", TreeStructureIcon, "/ledger/accounts", false, true),
  s("book", "ar", "nav.arLedger", ScalesIcon, "/ledger/ar", false, false),
  s("book", "events", "nav.ledgerEvents", LightningIcon, "/ledger/events", true, true),
  s("report", "trialBalance", "nav.ledgerTrialBalance", CalculatorIcon, "/ledger/reports/trial-balance", false, true),
  s("report", "balanceSheet", "nav.ledgerBalanceSheet", BankIcon, "/ledger/reports/balance-sheet", false, true),
  s("report", "incomeStatement", "nav.ledgerIncomeStatement", ChartLineUpIcon, "/ledger/reports/income-statement", false, true),
  s("report", "accountStatement", "nav.ledgerAccountStatement", ClipboardTextIcon, "/ledger/reports/account-statement", false, true),
  s("report", "arAging", "nav.ledgerArAging", HourglassIcon, "/ledger/reports/ar-aging", false, true),
  s("intercompany", "mirror", "nav.ledgerMirror", ArrowsLeftRightIcon, "/ledger/mirror", false, true),
  s("intercompany", "intercompany", "nav.ledgerIntercompany", ScalesIcon, "/ledger/intercompany", false, true),
  s("intercompany", "memo", "nav.ledgerMemo", PackageIcon, "/ledger/memo", false, true),
  s("settings", "templates", "nav.ledgerTemplates", SlidersIcon, "/ledger/templates", true, true),
  s("settings", "periods", "nav.ledgerPeriods", CalendarCheckIcon, "/ledger/periods", true, true),
  s("settings", "opening", "nav.ledgerOpening", UploadSimpleIcon, "/ledger/opening", true, true),
  s("settings", "settings", "nav.ledgerSettings", GearIcon, "/ledger/settings", false, false),
];

export const ledgerGroupTitleKey = (group: LedgerGroupKey) => `ledgerHub.groups.${group}`;

export const findLedgerSection = (pathname: string): LedgerSection | undefined =>
  LEDGER_SECTIONS.find((x) => pathname === x.link || pathname.startsWith(`${x.link}/`));
